import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIssueStore, useProjectStore } from '@/stores';
import type { IssueListType, IssueType, CreateIssueDTO, Attachment } from '@/types';
import { ISSUE_TYPE_LABELS } from '@/types';
import { Button } from '@/components/ui';
import { X, HelpCircle, Calendar, Truck, Ruler, Palette, Settings, MoreHorizontal, Paperclip, AlertCircle, Plus } from 'lucide-react';

interface IssueCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  issueType: IssueListType;
}

export function IssueCreateModal({ isOpen, onClose, issueType }: IssueCreateModalProps) {
  const { currentProject } = useProjectStore();
  const { createIssue, isLoading } = useIssueStore();

  const [formData, setFormData] = useState({
    description: '',
    type: 'dimension' as IssueType,
    openDate: new Date().toISOString().split('T')[0],
    vehicleNode: '',
    attachments: [] as Attachment[],
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showVehicleInput, setShowVehicleInput] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const datePickerTimeout = useRef<NodeJS.Timeout | null>(null);
  const vehicleInputTimeout = useRef<NodeJS.Timeout | null>(null);
  const typeMenuTimeout = useRef<NodeJS.Timeout | null>(null);

  const dateRef = useRef<HTMLDivElement>(null);
  const vehicleRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const newAttachments: Attachment[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      const formDataFile = new FormData();
      formDataFile.append('file', file);

      try {
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formDataFile,
        });

        const data = await response.json();
        
        if (data.url) {
          const attachment: Attachment = {
            id: crypto.randomUUID(),
            issueId: '',
            name: file.name,
            url: data.url,
            type: file.type,
            size: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: 'currentUser',
          };
          newAttachments.push(attachment);
        }
      } catch (error) {
        console.error('文件上传失败:', error);
      }
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));
    setIsUploading(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (id: string) => {
    const attachment = formData.attachments.find(a => a.id === id);
    if (attachment) {
      try {
        await fetch(`/api/upload?url=${encodeURIComponent(attachment.url)}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.error('删除文件失败:', error);
      }
    }
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== id),
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (vehicleRef.current && !vehicleRef.current.contains(event.target as Node)) {
        setShowVehicleInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasType = !!formData.type;
  const hasDate = !!formData.openDate;
  const hasVehicle = !!formData.vehicleNode;

  const handleSubmit = async () => {
    if (!currentProject || !formData.description) return;

    const dto: CreateIssueDTO = {
      projectId: currentProject.id,
      issueType,
      description: formData.description,
      type: formData.type,
      openDate: formData.openDate,
      vehicleNode: formData.vehicleNode || undefined,
      attachments: formData.attachments.map(a => ({
        name: a.name,
        url: a.url,
        type: a.type,
        size: a.size,
        uploadedAt: a.uploadedAt,
        uploadedBy: a.uploadedBy,
      })),
    };

    try {
      await createIssue(dto);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Create issue error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      type: 'dimension',
      openDate: new Date().toISOString().split('T')[0],
      vehicleNode: '',
      attachments: [],
    });
    setShowDatePicker(false);
    setShowVehicleInput(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200 rounded-t-xl">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-500" />
            {issueType === 'product' ? 'Create Product Issue' : 'Create Production Issue'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 px-4 pb-2">
          <div className="mt-4 mb-4 relative">
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none text-base pr-16 text-left align-top focus:outline-none focus:ring-0 focus:border-gray-300"
              rows={4}
              placeholder="请输入问题描述..."
            />
            <div className="absolute bottom-9 left-3 flex items-center gap-1">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center text-xs text-gray-500 hover:text-gray-700 transition-colors"
                title="上传附件"
              >
                <Paperclip className={`w-3 h-3 ${formData.attachments.length > 0 ? 'text-blue-500' : ''}`} />
              </button>
              {formData.attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center gap-1">
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                  >
                    {attachment.name}
                  </a>
                  <button
                    onClick={() => removeAttachment(attachment.id)}
                    className="text-xs text-gray-400 hover:text-red-500"
                    title="删除附件"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="absolute bottom-3 left-3 flex gap-2 text-xs">
              {hasDate && <span className="text-gray-600">{formData.openDate}</span>}
              {(hasDate && hasType) && <span className="text-gray-400">|</span>}
              {hasType && <span className="text-gray-600">{ISSUE_TYPE_LABELS[formData.type]}</span>}
              {(hasType && hasVehicle) && <span className="text-gray-400">|</span>}
              {hasVehicle && <span className="text-gray-600">{formData.vehicleNode}</span>}
            </div>
            <div className="absolute bottom-2 right-5 flex gap-2">
              <div 
                ref={dateRef} 
                className="relative flex flex-col items-center"
                onMouseEnter={() => {
                  if (datePickerTimeout.current) {
                    clearTimeout(datePickerTimeout.current);
                    datePickerTimeout.current = null;
                  }
                  setShowDatePicker(true);
                }}
                onMouseLeave={() => {
                  datePickerTimeout.current = setTimeout(() => {
                    setShowDatePicker(false);
                  }, 200);
                }}
              >
                <button
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    hasDate ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                </button>

                {showDatePicker && (
                  <div className="absolute -top-12 -left-2 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10">
                    <input
                      type="date"
                      value={formData.openDate}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, openDate: e.target.value }));
                        setShowDatePicker(false);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-xs outline-none"
                    />
                  </div>
                )}
              </div>

              <div 
                className="relative flex flex-col items-center"
                onMouseEnter={() => {
                  if (typeMenuTimeout.current) {
                    clearTimeout(typeMenuTimeout.current);
                    typeMenuTimeout.current = null;
                  }
                  setShowTypeMenu(true);
                }}
                onMouseLeave={() => {
                  typeMenuTimeout.current = setTimeout(() => {
                    setShowTypeMenu(false);
                  }, 200);
                }}
              >
                <div className="relative flex items-center justify-center">
                  <button
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all z-20 ${
                      hasType ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                    }`}
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>

                  <AnimatePresence>
                    {showTypeMenu && (
                      <motion.div
                        key="type-dimension"
                        initial={{ x: 0, y: 0, opacity: 0 }}
                        animate={{ x: -54, y: -40, opacity: 1 }}
                        exit={{ x: 0, y: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute"
                      >
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, type: 'dimension' }))}
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center group/option ${
                            formData.type === 'dimension' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Ruler className="w-4 h-4" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-xs text-gray-500 opacity-0 group-hover/option:opacity-100 transition-opacity">
                            {ISSUE_TYPE_LABELS.dimension}
                          </span>
                        </button>
                      </motion.div>
                    )}

                    {showTypeMenu && (
                      <motion.div
                        key="type-function"
                        initial={{ x: 0, y: 0, opacity: 0 }}
                        animate={{ x: -18, y: -40, opacity: 1 }}
                        exit={{ x: 0, y: 0, opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.05 }}
                        className="absolute"
                      >
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, type: 'function' }))}
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center group/option ${
                            formData.type === 'function' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Settings className="w-4 h-4" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-xs text-gray-500 opacity-0 group-hover/option:opacity-100 transition-opacity">
                            {ISSUE_TYPE_LABELS.function}
                          </span>
                        </button>
                      </motion.div>
                    )}

                    {showTypeMenu && (
                      <motion.div
                        key="type-appearance"
                        initial={{ x: 0, y: 0, opacity: 0 }}
                        animate={{ x: 18, y: -40, opacity: 1 }}
                        exit={{ x: 0, y: 0, opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                        className="absolute"
                      >
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, type: 'appearance' }))}
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center group/option ${
                            formData.type === 'appearance' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Palette className="w-4 h-4" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-xs text-gray-500 opacity-0 group-hover/option:opacity-100 transition-opacity">
                            {ISSUE_TYPE_LABELS.appearance}
                          </span>
                        </button>
                      </motion.div>
                    )}

                    {showTypeMenu && (
                      <motion.div
                        key="type-other"
                        initial={{ x: 0, y: 0, opacity: 0 }}
                        animate={{ x: 54, y: -40, opacity: 1 }}
                        exit={{ x: 0, y: 0, opacity: 0 }}
                        transition={{ duration: 0.2, delay: 0.15 }}
                        className="absolute"
                      >
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, type: 'other' }))}
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center group/option ${
                            formData.type === 'other' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap text-xs text-gray-500 opacity-0 group-hover/option:opacity-100 transition-opacity">
                            {ISSUE_TYPE_LABELS.other}
                          </span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div 
                ref={vehicleRef} 
                className="relative flex flex-col items-center"
                onMouseEnter={() => {
                  if (vehicleInputTimeout.current) {
                    clearTimeout(vehicleInputTimeout.current);
                    vehicleInputTimeout.current = null;
                  }
                  setShowVehicleInput(true);
                }}
                onMouseLeave={() => {
                  vehicleInputTimeout.current = setTimeout(() => {
                    setShowVehicleInput(false);
                  }, 200);
                }}
              >
                <button
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    hasVehicle ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  <Truck className="w-4 h-4" />
                </button>

                {showVehicleInput && (
                  <div className="absolute -top-12 -right-2 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10">
                    <input
                      type="text"
                      value={formData.vehicleNode}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicleNode: e.target.value }))}
                      onBlur={() => {
                        if (formData.vehicleNode) {
                          setShowVehicleInput(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setShowVehicleInput(false);
                        }
                      }}
                      className="w-28 px-2 py-1 border border-gray-300 rounded-lg text-xs outline-none"
                      placeholder="输入节点"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 flex justify-end border-t border-gray-200 rounded-b-xl">
          <button
            onClick={handleSubmit}
            disabled={!formData.description || isLoading}
            className="group flex items-center bg-transparent border-none cursor-pointer disabled:opacity-50"
          >
            <span className="relative pb-1 text-sm font-medium tracking-wider uppercase text-black pr-4">
              Add Issue
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
            </span>
            <svg
              className="w-5 h-4 text-black transform -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
